import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { usePlatformIdentity } from '@/shared/platform/platformIdentity';
import { useCallback, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Box, Center, Group, Text, rem } from '@mantine/core';
import {
  IconBriefcase,
  IconClockHour4,
  IconFileInvoice,
  IconUsers,
} from '@tabler/icons-react';
import type { ComponentType } from 'react';
import { isAuthenticatedSync } from '@/shared/lib/auth-state';
import { mw, MW_AUTH_BG, MW_FONT } from '@/shared/mobile/tokens';
import { env } from '@/shared/lib/env';
import { MaewayLogoLazy } from '@/shared/logo3d/MaewayLogoLazy';

export const Route = createFileRoute('/_auth')({
  beforeLoad: () => {
    if (isAuthenticatedSync()) {
      throw redirect({ to: '/' });
    }
  },
  component: AuthLayout,
});

/** Voci di valore del pannello brand (icona lineare + testo), MaeDesign §14. */
const FEATURES: { label: string; hint: string; icon: ComponentType<{ size?: number; stroke?: number }> }[] = [
  { label: 'Presenze', hint: 'Timbrature e turni', icon: IconClockHour4 },
  { label: 'Rendicontazione', hint: 'Ore e attività su progetto', icon: IconFileInvoice },
  { label: 'Progetti', hint: 'Progetti e pianificazione', icon: IconBriefcase },
  { label: 'HR', hint: 'Ferie, permessi e trasferte', icon: IconUsers },
];

/**
 * I nodi del pattern Maestrale, con gli archi che li legano.
 *
 * Erano `<circle>` e `<line>` scritti a mano con le coordinate ripetute due volte: spostare un
 * nodo voleva dire ritrovare a mano le righe che lo nominavano. Ora gli archi sono coppie di
 * indici, quindi le linee **seguono** i nodi ovunque li si porti.
 */
interface Node { x: number; y: number; r: number; lightBlue: boolean; drift: 'A' | 'B' }

const INITIAL_NODES: Node[] = [
  { x: 250, y: 90, r: 6, lightBlue: true, drift: 'A' },
  { x: 150, y: 360, r: 7, lightBlue: true, drift: 'A' },
  { x: 340, y: 700, r: 6, lightBlue: true, drift: 'A' },
  { x: 500, y: 560, r: 5, lightBlue: true, drift: 'A' },
  { x: 70, y: 150, r: 5, lightBlue: false, drift: 'B' },
  { x: 470, y: 180, r: 5, lightBlue: false, drift: 'B' },
  { x: 420, y: 440, r: 6, lightBlue: false, drift: 'B' },
  { x: 90, y: 620, r: 4.5, lightBlue: false, drift: 'B' },
];

const EDGES: [number, number][] = [[4, 0], [0, 5], [4, 1], [1, 6], [6, 5], [1, 7], [7, 2], [2, 3], [6, 3]];

const VB_W = 560;
const VB_H = 820;

/**
 * Pattern Maestrale — rete di nodi/vento (MaeDesign §7), con i nodi **afferrabili**.
 *
 * Due `<svg>` sovrapposti e non uno solo: il disegno resta **dietro** al claim e alle voci di
 * valore, dov'era; le maniglie stanno **sopra**, invisibili e più larghe dei pallini. Con un
 * livello solo o i nodi finivano davanti al testo, o metà di loro sarebbe stata inafferrabile
 * perché coperta.
 */
function PatternNodes() {
  const [nodes, setNodes] = useState(() => INITIAL_NODES.map((n) => ({ ...n })));
  const handlesRef = useRef<SVGSVGElement>(null);
  const taken = useRef<{ i: number; dx: number; dy: number } | null>(null);

  /**
   * Da pixel di schermo a coordinate del viewBox. Serve la matrice vera dell'SVG: con
   * `preserveAspectRatio="slice"` il disegno è scalato **e** ritagliato, e una proporzione
   * calcolata a mano sui lati sbaglierebbe di tutto il ritaglio.
   */
  const inCoordinates = useCallback((e: { clientX: number; clientY: number }) => {
    const svg = handlesRef.current;
    const m = svg?.getScreenCTM?.();
    if (!svg || !m) return null;
    const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(m.inverse());
    return p;
  }, []);

  const down = (i: number) => (e: ReactPointerEvent<SVGCircleElement>) => {
    const p = inCoordinates(e);
    if (!p) return;
    taken.current = { i, dx: nodes[i].x - p.x, dy: nodes[i].y - p.y };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* puntatore già sparito */ }
    e.preventDefault();
  };

  const move = (e: ReactPointerEvent<SVGCircleElement>) => {
    const g = taken.current;
    if (!g) return;
    const p = inCoordinates(e);
    if (!p) return;
    const x = Math.max(10, Math.min(VB_W - 10, p.x + g.dx));
    const y = Math.max(10, Math.min(VB_H - 10, p.y + g.dy));
    setNodes((prec) => prec.map((n, i) => (i === g.i ? { ...n, x, y } : n)));
  };

  const su = (e: ReactPointerEvent<SVGCircleElement>) => {
    if (!taken.current) return;
    taken.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* già rilasciato */ }
  };

  const common = {
    viewBox: `0 0 ${VB_W} ${VB_H}`,
    preserveAspectRatio: 'xMidYMid slice' as const,
  };

  return (
    <>
      <svg
        {...common}
        aria-hidden
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.6, pointerEvents: 'none' }}
      >
        <g stroke="rgba(255,255,255,0.22)" strokeWidth="0.8" fill="none">
          {EDGES.map(([a, b]) => (
            <line key={`${a}-${b}`} x1={nodes[a].x} y1={nodes[a].y} x2={nodes[b].x} y2={nodes[b].y} />
          ))}
        </g>
        {nodes.map((n, i) => (
          <circle
            key={i}
            className={n.drift === 'A' ? 'mw-auth-nA' : 'mw-auth-nB'}
            cx={n.x}
            cy={n.y}
            r={n.r}
            fill={n.lightBlue ? mw.accent : 'rgba(255,255,255,0.9)'}
          />
        ))}
      </svg>

      {/* Maniglie: raggio 18 e riempimento trasparente — i pallini veri arrivano a 4,5 px e
          a quella misura non si prendono. `pointerEvents` acceso solo qui, così il resto del
          pannello (testo selezionabile compreso) non se ne accorge. */}
      <svg
        {...common}
        ref={handlesRef}
        aria-hidden
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 2, pointerEvents: 'none' }}
      >
        {nodes.map((n, i) => (
          <circle
            key={i}
            cx={n.x}
            cy={n.y}
            r={18}
            fill="transparent"
            style={{ pointerEvents: 'auto', cursor: 'grab', touchAction: 'none' }}
            onPointerDown={down(i)}
            onPointerMove={move}
            onPointerUp={su}
            onPointerCancel={su}
          />
        ))}
      </svg>
    </>
  );
}

/**
 * Il marchio senza 3D: l'iniziale in un quadrato bordeaux e il nome.
 *
 * Era scritto inline nella testata mobile; ora è un componente perché serve in tre momenti —
 * mentre il chunk del logo si scarica, se il dispositivo non ha WebGL, e sotto i 768px dove il
 * pannello brand di sinistra non c'è. Tre copie della stessa cosa sarebbero divergute.
 */
function StaticBrand({ appName, size = 56 }: { appName: string; size?: number }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: size / 3.5, margin: '0 auto',
      background: 'linear-gradient(150deg, var(--brand-fill), var(--brand-grad-2))',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--on-brand)', fontWeight: 700, fontSize: size * 0.45,
      boxShadow: '0 8px 22px rgba(84,4,9,0.28)',
    }}>
      {appName.charAt(0).toUpperCase()}
    </div>
  );
}

/**
 * Layout auth con pannello brand split (MaeDesign — template `login.html`):
 * a sinistra il "brand moment" — pannello bordeaux con **pattern Maestrale**
 * animato (rete di nodi/vento, §7), wordmark, claim e voci di valore; a destra
 * la form (Outlet) su superficie chiara con leggera profondità. Tutte le pagine
 * auth ereditano questa cornice. Il pannello è nascosto sotto md: su mobile una
 * testata brand compatta lo sostituisce.
 */
function AuthLayout() {
  const platform = usePlatformIdentity();
  const appName = env.VITE_APP_NAME;

  return (
    /* `dvh` e non `vh`: su mobile `100vh` misura la finestra **senza** la barra degli indirizzi,
       quindi vale piu' dello spazio davvero visibile e garantisce uno scroll di qualche decina di
       pixel anche quando il contenuto ci starebbe. `minHeight` resta doppio per i browser che
       `dvh` non lo conoscono: il secondo vince dove e' supportato. */
    <Box style={{ display: 'flex', minHeight: '100dvh', background: MW_AUTH_BG, fontFamily: MW_FONT }}>
      <style>{`
        @keyframes mwAuthFloatA { 0%,100% { transform: translateY(0) } 50% { transform: translateY(9px) } }
        @keyframes mwAuthFloatB { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-8px) } }
        .mw-auth-nA { animation: mwAuthFloatA 7.5s ease-in-out infinite }
        .mw-auth-nB { animation: mwAuthFloatB 9.5s ease-in-out infinite }
        /* La deriva sposta il disegno ma non la maniglia: tenuta piccola, resta dentro
           i 18 px del bersaglio e il pallino si prende comunque al primo colpo. */
        @media (prefers-reduced-motion: reduce) { .mw-auth-nA, .mw-auth-nB { animation: none } }

        /*
         * Il ritmo verticale della colonna sotto il breakpoint md si misura in altezza di schermo, non in
         * pixel fissi: il login deve entrare intero senza scorrere, e un valore tarato su un
         * telefono da 812 px sfora su uno da 667. Sopra md non cambia niente — li' lo spazio
         * c'e', e il marchio resta grande com'era.
         */
        .mw-auth-logo { height: 132px; margin-bottom: 6px; }
        .mw-auth-testata { margin-bottom: 26px; }
        @media (max-width: 991px) {
          .mw-auth-logo { height: clamp(76px, 12vh, 132px); margin-bottom: 4px; }
          .mw-auth-testata { margin-bottom: clamp(10px, 2vh, 26px); }
          /* Il padding orizzontale resta quello di prima (scala sulla larghezza); il verticale
             passa all'altezza, perche' su uno schermo basso 40 px sopra e 40 sotto sono 80 px
             tolti al contenuto. Sopra md non si tocca: li' lo spazio non manca. */
          .mw-auth-colonna-esterna { padding-top: clamp(12px, 2.5vh, 40px); padding-bottom: clamp(12px, 2.5vh, 40px); }
        }

        /*
         * Le spaziature della card di accesso. Stanno qui e non nelle prop perche' in Mantine 7
         * ne' gap ne' p accettano valori per breakpoint.
         *
         * Si scrivono sia la variabile di Mantine (--stack-gap, che e' cio' che lo Stack legge)
         * sia la proprieta': la prima e' il canale sanzionato, la seconda regge anche se un
         * aggiornamento cambia il nome della variabile.
         */
        .mw-auth-colonna { --stack-gap: 32px; gap: 32px; }
        .mw-auth-card { padding: 34px; }
        .mw-auth-campi { --stack-gap: 16px; gap: 16px; }
        @media (max-width: 767px) {
          .mw-auth-colonna { --stack-gap: 16px; gap: 16px; }
          .mw-auth-card { padding: 20px; }
          .mw-auth-campi { --stack-gap: 12px; gap: 12px; }
        }

        /*
         * Schermi bassi (iPhone SE e simili, 667 px e sotto): il logo 3D esce di scena.
         *
         * A quell'altezza la somma non torna comunque — marchio 80 + testata 82 + card 529 non
         * stanno in 667 — e fra le tre cose il logo e' quella che si puo' togliere lasciando
         * intatto il resto: il nome dell'applicazione resta scritto appena sotto, e chi entra
         * vede la form per intero invece di doverla cercare scorrendo.
         *
         * Si sceglie in base all'ALTEZZA e non alla larghezza: e' l'altezza a mancare, e un
         * telefono ruotato ha lo stesso problema di uno piccolo.
         *
         * La soglia e' 820 e non 720 per un motivo misurato: a 740 px il logo rientrava e la
         * pagina tornava a sforare di 48: fra i due gradini restava una fascia di schermi in cui
         * il difetto era ancora tutto li'. Senza il marchio il contenuto sta in ~690 px, quindi da
         * 821 in su (844, 852, 915, 926: ogni telefono recente) il logo si vede e la pagina entra
         * lo stesso.
         */
        @media (max-height: 820px) {
          .mw-auth-logo { display: none; }
          .mw-auth-card { padding: 16px; }
          .mw-auth-campi { --stack-gap: 8px; gap: 8px; }
          .mw-auth-colonna { --stack-gap: 10px; gap: 10px; }
        }

        /*
         * Sotto i 660 px (Android piccoli, 360x640) restano 18 px di troppo anche senza il logo.
         * Va la riga descrittiva: il nome dell'applicazione resta, e quella frase la si legge una
         * volta sola nella vita — la form la si usa tutte le mattine.
         */
        @media (max-height: 660px) {
          .mw-auth-testata > :last-child { display: none; }
          .mw-auth-testata { margin-bottom: 8px; }
        }
      `}</style>

      <Box
        visibleFrom="md"
        style={{
          position: 'relative',
          overflow: 'hidden',
          width: '46%',
          maxWidth: 560,
          background: 'linear-gradient(155deg, var(--brand-fill) 0%, var(--brand-grad-2) 100%)',
          padding: '56px 52px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <PatternNodes />

        {/* Wordmark (logo grande in testa, MaeDesign §6). */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <Group gap={12} wrap="nowrap" align="center">
            <div style={{
              width: rem(46), height: rem(46), borderRadius: 12, flexShrink: 0,
              background: mw.surface, color: mw.brand,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: 'var(--fs-h3)', letterSpacing: '-0.02em',
            }}>
              {appName.charAt(0).toUpperCase()}
            </div>
            <Text style={{ fontSize: 'var(--fs-h2)', fontWeight: 700, color: 'var(--on-brand)', letterSpacing: '-0.02em', lineHeight: 1 }}>
              {appName}
            </Text>
          </Group>
        </div>

        {/* Claim + voci di valore. */}
        <div style={{ position: 'relative', zIndex: 1, maxWidth: 380 }}>
          <Text style={{ fontSize: 'var(--fs-display)', fontWeight: 700, color: 'var(--on-brand)', letterSpacing: '-0.025em', lineHeight: 1.12 }}>
            Tutto il tuo lavoro,<br />in un unico spazio.
          </Text>
          <Text style={{ fontSize: 'var(--fs-body)', fontWeight: 500, color: 'rgba(255,255,255,0.78)', marginTop: 16, lineHeight: 1.55 }}>
            La piattaforma per presenze, rendicontazione, progetti e trasferte.
          </Text>

          <div style={{ marginTop: 34, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {FEATURES.map(({ label, hint, icon: Icon }) => (
              <Group key={label} gap={13} wrap="nowrap" align="center">
                <div style={{
                  width: 40, height: 40, borderRadius: 11, flexShrink: 0,
                  background: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.16)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--on-brand)',
                }}>
                  <Icon size={21} stroke={1.8} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <Text style={{ fontSize: 'var(--fs-body-lg)', fontWeight: 600, color: 'var(--on-brand)', lineHeight: 1.2 }}>{label}</Text>
                  <Text style={{ fontSize: 'var(--fs-compact)', color: 'rgba(255,255,255,0.68)', lineHeight: 1.3 }}>{hint}</Text>
                </div>
              </Group>
            ))}
          </div>
        </div>

        {/* Footer istituzionale. */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Qui c'era «© 2026 Maestrale Group», cablato. Ora i diritti li scrive il superadmin
              nell'identità della piattaforma (task 2061): vuoti, non si mostra niente. */}
          {platform.copyright && (
            <Text style={{ fontSize: 'var(--fs-compact)', fontWeight: 500, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.01em' }}>
              {platform.copyright}
            </Text>
          )}
        </div>
      </Box>

      {/* `minWidth: 0` non è cosmetico: un flex item parte da `min-width: auto`, cioè non
          scende sotto la larghezza minima del proprio contenuto. Con i 40px di padding fissi
          questa colonna pretendeva 470px su uno schermo da 375 — la pagina di login slittava
          di lato e il form entrava tagliato a sinistra. Il padding ora scala con lo schermo. */}
      <Center className="mw-auth-colonna-esterna" style={{ flex: 1, minWidth: 0, padding: 'clamp(16px, 5vw, 40px)' }}>
        <Box style={{ width: '100%', maxWidth: 430 }}>
          {/*
            Il logo 3D sta **sopra la form**, sul fondo chiaro, e non nel pannello bordeaux di
            sinistra: il marchio è azzurro e bordeaux, e il bordeaux su bordeaux sparisce.
            Qui si vede a ogni larghezza, ed è la prima cosa che si guarda entrando.

            Altezza fissa sul contenitore, non sul canvas: il componente riempie il padre, e
            senza un'altezza dichiarata collasserebbe a zero.
          */}
          <div className="mw-auth-logo">
            <MaewayLogoLazy
              clock
              autoPlay={false}
              dragToRotate
              padding={1.04}
              fallback={<div style={{ paddingTop: 30 }}><StaticBrand appName={appName} /></div>}
            />
          </div>

          {/* Testata brand mobile (il pannello bordeaux a sinistra è nascosto sotto md).
              Il quadrato con l'iniziale non c'è più: sopra c'è già il marchio, e due volte
              lo stesso segno a venti pixel di distanza è rumore, non identità. */}
          <Box hiddenFrom="md" className="mw-auth-testata" style={{ textAlign: 'center' }}>
            <Text style={{ fontSize: 'var(--fs-h2)', fontWeight: 700, color: mw.brand, letterSpacing: '-0.02em' }}>
              {appName}
            </Text>
            <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, marginTop: 4, lineHeight: 1.5 }}>
              La piattaforma per presenze, rendicontazione e progetti.
            </Text>
          </Box>
          <Outlet />
        </Box>
      </Center>
    </Box>
  );
}
